import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useHoaDonDieuChinhGiaHangBan } from '@/hooks/queries/useHoaDonDieuChinhGiaHangBan';
import type { SearchValues } from '@/components/custom/erp/data-table-v2';

interface HoaDonDieuChinhGiaHangBanSearchContextValue {
  searchState: {
    filterParams: SearchValues;
    searchResults: BanHangRecord[];
    isSearching: boolean;
  };
  handleSearchChange: (values: SearchValues) => void;
  resolvedRows: BanHangRecord[];
}

const HoaDonDieuChinhGiaHangBanSearchContext = createContext<HoaDonDieuChinhGiaHangBanSearchContextValue | null>(null);

export function useHoaDonDieuChinhGiaHangBanSearchContext() {
  const context = useContext(HoaDonDieuChinhGiaHangBanSearchContext);
  if (!context) {
    throw new Error(
      'useHoaDonDieuChinhGiaHangBanSearchContext must be used within HoaDonDieuChinhGiaHangBanSearchProvider'
    );
  }
  return context;
}

export function HoaDonDieuChinhGiaHangBanSearchProvider({ children }: { children: ReactNode }) {
  const [searchState, setSearchState] = useState<{
    filterParams: SearchValues;
    searchResults: BanHangRecord[];
    isSearching: boolean;
  }>({
    filterParams: {},
    searchResults: [],
    isSearching: false
  });

  const { filterHoaDonDieuChinhGiaHangBans } = useHoaDonDieuChinhGiaHangBan();

  const handleSearchChange = useCallback((values: SearchValues) => {
    const nextHasFilters = Object.keys(values || {}).length > 0;

    if (!nextHasFilters) {
      setSearchState({
        filterParams: {},
        searchResults: [],
        isSearching: false
      });
      return;
    }

    setSearchState(prev => ({
      ...prev,
      filterParams: values,
      isSearching: false,
      searchResults: []
    }));
  }, []);

  // Fetch filtered data when filterParams change
  useEffect(() => {
    const fetchFilteredData = async () => {
      if (!searchState.filterParams || Object.keys(searchState.filterParams).length === 0) {
        return;
      }

      setSearchState(prev => ({ ...prev, isSearching: true }));

      try {
        const transformedParams = Object.entries(searchState.filterParams).reduce((acc, [key, value]) => {
          acc[key] = Array.isArray(value) ? value : [value];
          return acc;
        }, {} as BanHangSearchParams);

        const res = await filterHoaDonDieuChinhGiaHangBans(transformedParams);
        setSearchState(prev => ({
          ...prev,
          searchResults: res || [],
          isSearching: false
        }));
      } catch (error) {
        console.error('Error fetching filtered data:', error);
        setSearchState(prev => ({
          ...prev,
          searchResults: [],
          isSearching: false
        }));
      }
    };

    fetchFilteredData();
  }, [searchState.filterParams, filterHoaDonDieuChinhGiaHangBans]);

  const resolvedRows = searchState.searchResults;

  return (
    <HoaDonDieuChinhGiaHangBanSearchContext.Provider
      value={{
        searchState,
        handleSearchChange,
        resolvedRows
      }}
    >
      {children}
    </HoaDonDieuChinhGiaHangBanSearchContext.Provider>
  );
}
